import React, { Fragment, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import QRCode from 'qrcode.react';
import classNames from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { arrayMove } from '@dnd-kit/sortable';
import { useMutation, useQuery } from 'react-apollo';
import { useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import qs from 'query-string';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
} from '@dnd-kit/core';

import DesignEditor from '../../../../../../components/global/DesignEditor';
import DesignEditorHeader, {
  DesignEditorHeaderLeftSlot,
  DesignEditorHeaderRightSlot,
} from '../../../../../../components/global/DesignEditor/components/DesignEditorHeader';
import Button from '../../../../../../components/global/Button';
import Angle from '../../../../../../components/global/Angle';
import DesignEditorControls, {
  DesignEditorPrimaryControls,
  DesignEditorSecondaryControls,
} from '../../../../../../components/global/DesignEditor/components/DesignEditorControls';
import DesignEditorPrimaryControlButton from '../../../../../../components/global/DesignEditor/components/DesignEditorControls/components/DesignEditorPrimaryControlButton';
import SecondaryBackgroundView from './components/SecondaryBackgroundView';
import BackgroundIcon from '../../../../../../components/icons/BackgroundIcon';
import DesignEditorPreviewPane, {
  DesignEditorPreviewContent,
} from '../../../../../../components/global/DesignEditor/components/DesignEditorPreviewPane';
import useAlbumSlideshow from '../hooks/useAlbumSlideshow';
import Spinner from '../../../../../../components/global/Spinner';
import useOpenModal from '../../../../../../hooks/useOpenModal';
import useCloseModal from '../../../../../../hooks/useCloseModal';
import useRequestConfirm from '../../../../../../hooks/useRequestConfirm';
import PhotoOrderingOverlayModal, {
  ALBUM_SLIDESHOW_DESIGNER_PHOTO_ORDERING_MODAL,
} from './components/PhotoOrderingOverlayModal';
import useSlideshowPlayback from '../../../../../../components/grid/components/AlbumSlideshowGridViewer/hooks/useSlideshowPlayback';
import { calculateSlideshowDuration } from '../../../../../../components/grid/components/AlbumSlideshowGridViewer/helpers';
import { breakpointDown } from '../../../../../../services/responsive/responsive';
import {
  ALBUM_SLIDESHOW_MODE,
  ALBUM_SLIDESHOW_STATUS,
} from '../../../../../../constants/albums';
import { getMaxThumb } from '../../../../../../util/photo';
import AlbumSlideshowShareModal, {
  ALBUM_SLIDESHOW_SHARE_MODAL,
} from '../../../../../../components/global/AlbumSlideshowShareModal';
import { getErrorString } from '../../../../../../util/normalize-errors';
import {
  isNativeAppWebView,
  postNativeAppAlbumSlideshowDesignerUpdateError,
  postNativeAppAlbumSlideshowDesignerShareDialog,
  postNativeAppAlbumSlideshowDesignerDismiss,
} from '../../../../../../services/native-bridge/native-apps-interface';
import {
  CONTRIBUTOR_UPLOAD_ACCESS,
  CONTRIBUTOR_VIEW_ACCESS,
} from '../../../../../../constants/contributors';
import { QR_CODE_INSERT_MODAL } from './components/QRCodeInsertModal';
import { Photo } from './components/PhotoOrderingOverlayModal/components/PhotoSortable';
import AccountCardEditor from './components/AccountCardEditor';
import { getSlideshowQRInsertUrl, isIntroLayout } from './helpers';
import IntroSlideLabel from '../../MemoryBookAlbum/components/MemoryBookIntroSlide/components/IntroSlideLabel';
import SlideshowUploadAccessModal, {
  SLIDESHOW_UPLOAD_ACCESS_MODAL,
} from './components/SlideshowUploadAccessModal';
import { contributorAssignmentsUpdate as contributorAssignmentsUpdateMutation } from '../../../../../../graphql/mutations/contributor-assignments';
import useAlbumSlideshowForPendingLayouts from '../hooks/useAlbumSlideshowForPendingLayouts';
import useOpenAlert from '../../../../../../hooks/useOpenAlert';
import getConfig from '../../../../../../util/config';
import SecondaryTickerTapeView from './components/SecondaryTickerTapeView';
import TickerTapeIcon from '../../../../../../components/icons/TickerTapeIcon';
import SecondaryQRCodeView from './components/SecondaryQRCodeView/SecondaryQRCodeView';
import QRIcon from '../../../../../../components/icons/QRIcon';
import SecondarySlideDurationView from './components/SecondarySlideDurationView';
import StopWatchIcon from '../../../../../../components/icons/StopWatchIcon';
import { CONTRIBUTOR_SHARE_MODAL } from '../../../../../../constants/modals';
import ContributorShare from '../../../../components/ContributorShare';
import SlideshowIcon from '../../../../../../components/icons/SlideshowIcon';
import ContactCircleSaveModal from '../../../../../../components/global/ContactCircleSaveModal/modal';
import ContactCircleShareModal from '../../../../../../components/global/ContactCircleShareModal';
import EditableTickerTape from './components/EditableTickerTape';
import IntroLayoutConfigurator from './components/SecondaryIntroSlideView/components/IntroLayoutConfigurator/IntroLayoutConfigurator';
import PhotosIcon from '../../../../../../components/icons/PhotosIcon';
import SecondaryPhotosView from './components/SecondaryPhotosView';
import AlbumFolderUploadSuccessModal from '../../../AlbumFolder/AlbumFolders/components/AlbumFolderUploadSuccess/modal';
import {
  isAllPhotosSlideshow,
  isAutoLayoutsSlideshow,
  isMemoryBook,
  isSelectedPhotosSlideshow,
} from '../../../../../../util/album';
import { MeQuery } from '../../../../../../graphql/queries/account';
import { isAlbumOwner } from '../../../../../../util/account';
import { getAsset } from '../../../../../../util/assets';
import useCanRegenerateSlideshow from './hooks/useCanRegenerateSlideshow';
import { SLIDESHOW_REGENERATION_TRESHOLD_MINS } from './constants';

const CONTROL_PANE = {
  PHOTOS: 'PHOTOS',
  INTRO_SLIDE: 'INTRO_SLIDE',
  PHOTO_ORDER: 'PHOTO_ORDER',
  ELEMENTS: 'ELEMENTS',
  QR_CODE: 'QR_CODE',
  TICKER_TAPE: 'TICKER_TAPE',
  SLIDE_DURATION: 'SLIDE_DURATION',
  SLIDES: 'SLIDES',
  BACKGROUND: 'BACKGROUND',
};

const propTypes = {
  basename: PropTypes.string,
  history: PropTypes.shape({
    replace: PropTypes.func,
    push: PropTypes.func,
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      albumId: PropTypes.string,
    }),
  }),
  location: PropTypes.shape({
    search: PropTypes.string,
  }),
};

const AlbumSlideshowDesignerView = ({ basename, history, match, location }) => {
  const { albumId } = match.params;
  const { app } = getConfig();
  const isNativeView = isNativeAppWebView();
  const isMobile = useMediaQuery(breakpointDown('sm'));
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [editingIntroLayout, setEditingIntroLayout] = useState(false);
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const { defaultAlbumPhotoFilter } = qs.parse(location.search);
  const [activeControlPane, setActiveControlPane] = useState(
    CONTROL_PANE.PHOTOS,
  );
  const {
    saving,
    album,
    slideshow,
    refetchSlideshow,
    onSlideshowUpdate,
    loadingMore,
    onLoadMore,
  } = useAlbumSlideshow({ albumId });
  const {
    canRegenerateSlideshow,
    regenerateThresholdTimeLeft,
    refetchSlideshowForLastRegenerated,
  } = useCanRegenerateSlideshow({ albumId });
  const { isPollingPendingLayouts, onStartPollingPendingLayouts } =
    useAlbumSlideshowForPendingLayouts({
      albumId,
      onPollingFinished: () => {
        refetchSlideshow();
      },
    });
  const openModal = useOpenModal();
  const openAlert = useOpenAlert();
  const closeModal = useCloseModal();
  const requestConfirm = useRequestConfirm();
  const { data: { me: user } = {} } = useQuery(MeQuery, {
    skip: !authenticated,
  });
  const [contributorAssignmentsUpdate] = useMutation(
    contributorAssignmentsUpdateMutation,
  );

  // Slideshow updating related.
  const [slideshowSlides, setSlideshowSlides] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [excludedPhotos, setExcludedPhotos] = useState([]);
  const [deletedSlideIds, setDeletedSlideIds] = useState([]);
  const [slideDurationMs, setSlideDurationMs] = useState(5000);
  const [activeSlide, setActiveSlide] = useState(null);
  const [draggingSlidePhoto, setDraggingSlidePhoto] = useState(null);
  const [qrCode, setQrCode] = useState({ isEnabled: false });
  const [tickerTape, setTickerTape] = useState({ message: '' });
  const [tickerTapeEnabled, setTickerTapeEnabled] = useState(true);
  const [slideBackgroundColor, setSlideBackgroundColor] = useState('#000000');
  const [slideBackgroundImage, setSlideBackgroundImage] = useState(null);
  const isMemoryBookBool = isMemoryBook(album);
  const isAutoLayout = isAutoLayoutsSlideshow(slideshow);
  const isAllPhotos = isAllPhotosSlideshow(slideshow);
  const isSelectedPhotos = isSelectedPhotosSlideshow(slideshow);
  const slides = isAutoLayout ? slideshow?.layouts : slideshow?.photos;
  const tickerTapeMessageRef = useRef();
  const { upload_access: currentUploadAccess } =
    album?.auto_contributor_settings ?? {};

  useEffect(() => {
    setIsFirstRender(false);
  }, []);

  useEffect(() => {
    if (activeControlPane !== CONTROL_PANE.PHOTOS) {
      setEditingIntroLayout(false);
      if (!activeSlide) {
        setActiveSlide(slideshowSlides?.[0] ?? null);
      }
    }
  }, [activeControlPane]);

  useEffect(() => {
    if (activeSlide) {
      setEditingIntroLayout(false);
    }
  }, [activeSlide]);

  useEffect(() => {
    if (tickerTapeEnabled && !isFirstRender) {
      tickerTapeMessageRef?.current?.focus();
    }
  }, [tickerTapeEnabled]);

  useEffect(() => {
    if (slideshow?.slide_duration_ms) {
      setSlideDurationMs(slideshow.slide_duration_ms);
    }
    if (slides) {
      setSlideshowSlides(
        slideshow?.intro_layout && !isAutoLayout
          ? [slideshow?.intro_layout, ...slides?.records]
          : slides?.records,
      );

      if (
        !activeSlide &&
        !editingIntroLayout &&
        (slides?.records ?? []).length > 0
      ) {
        setActiveSlide(
          slideshow?.intro_layout && !isAutoLayout
            ? slideshow.intro_layout
            : slides.records[0],
        );
      }

      if (
        activeSlide &&
        slideshow?.intro_layout &&
        slideshow.intro_layout !== activeSlide &&
        !(slides?.records ?? []).find(
          ({ photo, account_card: card }) =>
            (photo && photo?.id === activeSlide?.photo?.id) ||
            (card && card?.id === activeSlide?.account_card?.id),
        )
      ) {
        setActiveSlide(slideshow?.intro_layout ?? slides?.records?.[0] ?? null);
      }
    }
    if (slideshow?.excluded_photos) {
      setExcludedPhotos(slideshow.excluded_photos.records);
    }
    if (slideshow?.selected_photos) {
      setSelectedPhotos(slideshow.selected_photos.records);
    }
    if (slideshow?.slide_background_color) {
      setSlideBackgroundColor(slideshow.slide_background_color);
    }
    // This can be null, so we need to account for it.
    if (typeof slideshow?.slide_background_asset_image !== 'undefined') {
      setSlideBackgroundImage(slideshow.slide_background_asset_image);
    }
    if (slideshow?.ticker) {
      setTickerTape({
        message: slideshow.ticker.message,
        color: slideshow.ticker.color,
        backgroundColor: slideshow.ticker.background_color,
      });

      const tickerHasContent =
        !!slideshow.ticker.message ||
        !!slideshow.ticker.color ||
        !!slideshow.ticker.background_color;

      if (!tickerTapeEnabled && tickerHasContent) {
        setTickerTapeEnabled(true);
      } else if (!tickerHasContent) {
        setTickerTapeEnabled(false);
      }
    }

    if (slideshow?.qr_code) {
      setQrCode({
        url: getSlideshowQRInsertUrl(app?.baseUrl, album),
        isEnabled: slideshow.qr_code.is_enabled,
        withCallout: slideshow.qr_code.display_callout,
        withReviewPhotos:
          album?.auto_contributor_settings?.upload_access ===
          CONTRIBUTOR_UPLOAD_ACCESS.REVIEW,
      });
    }
    // Reset deleted slide ids, since the slideshow has been updated
    // this avoids the issue of having deleted ids stored and then
    // trying to delete them.
    setDeletedSlideIds([]);
  }, [album, slideshow]);

  const activeSlideIdx = slideshowSlides.findIndex(
    ({ asset_image, photo, account_card }) =>
      (photo && activeSlide?.photo?.id === photo.id) ||
      (asset_image && activeSlide?.asset_image?.id === asset_image.id) ||
      (account_card && activeSlide?.account_card?.id === account_card.id),
  );
  const nextActiveSlideId = (() => {
    if (activeSlideIdx === -1) {
      return null;
    }
    // Since this will always be the 2nd or greatest, don't care for asset_image
    if (activeSlideIdx >= 0 && activeSlideIdx < slideshowSlides.length - 1) {
      return (
        slideshowSlides[activeSlideIdx + 1].photo?.id ??
        slideshowSlides[activeSlideIdx + 1].account_card?.id
      );
    }

    return (
      slideshowSlides[0].photo?.id ??
      slideshowSlides[0].asset_image?.id ??
      slideshowSlides[0].account_card?.id
    );
  })();

  const {
    isPlaying,
    onPlaybackStop,
    onThumbnailClick,
    slideDuration,
    slideRemainingTime,
    skipFadeIn,
  } = useSlideshowPlayback({
    slideshowSlides,
    slideshowSlide: activeSlide,
    slideshowSlideIdx: slideshowSlides.findIndex(
      ({ asset_image, photo, account_card }) =>
        (photo && activeSlide?.photo?.id === photo.id) ||
        (asset_image && activeSlide?.asset_image?.id === asset_image.id) ||
        (account_card && activeSlide?.account_card?.id === account_card.id),
    ),
    nextSlideId: nextActiveSlideId,
    slideDurationMs,
    slideshowDuration:
      typeof slideshow?.duration !== 'undefined'
        ? slideshow.duration * 1000
        : calculateSlideshowDuration(slideshowSlides, slideDurationMs),
    autoPlay: false,
    allowDeselect: true,
    intervalValue: 50,
    onChangeSlide: ({ nextSlideId }) => {
      if (nextSlideId !== null) {
        setActiveSlide(
          slideshowSlides.find(
            ({ asset_image, photo, account_card }) =>
              (photo && nextSlideId === photo.id) ||
              (asset_image && nextSlideId === asset_image.id) ||
              (account_card && nextSlideId === account_card.id),
          ),
        );
      }
    },
  });

  const photoOpacity = (() => {
    if (!isPlaying) {
      return 1.0;
    }

    const fadeThreshold = slideDuration * 0.2;
    // If we have > duration - threshold remaining time (fade-out)
    if (!skipFadeIn && slideRemainingTime > slideDuration - fadeThreshold) {
      return (slideDuration - slideRemainingTime) / 1000.0;
    }
    // If we have < remaining time (fade-out)
    if (slideRemainingTime <= fadeThreshold) {
      return slideRemainingTime / fadeThreshold;
    }
    return 1.0;
  })();

  const onSaveSlideshow = async (
    status = ALBUM_SLIDESHOW_STATUS.DRAFT,
    mode = ALBUM_SLIDESHOW_MODE.ALL_PHOTOS,
    currentExcludedPhotos = excludedPhotos,
    currentSelectedPhotos = selectedPhotos,
    createPhotos = [],
    updateLayouts = [],
    createAutoLayouts = false,
  ) => {
    try {
      const createExcludedPhotos =
        mode === ALBUM_SLIDESHOW_MODE.ALL_PHOTOS
          ? currentExcludedPhotos
              .filter(
                slideshowPhoto =>
                  !(slideshow?.excluded_photos?.records ?? []).find(
                    ({ photo }) => photo.id === slideshowPhoto.photo.id,
                  ),
              )
              .map(slideshowPhoto => ({
                photo_id: slideshowPhoto.photo?.id,
              }))
          : undefined;

      const deleteExcludedPhotos =
        mode === ALBUM_SLIDESHOW_MODE.ALL_PHOTOS
          ? (slideshow?.excluded_photos?.records ?? [])
              .filter(
                ({ photo }) =>
                  !currentExcludedPhotos.find(
                    slideshowPhoto => photo.id === slideshowPhoto.photo.id,
                  ),
              )
              .map(slideshowPhoto => ({
                photo_id: slideshowPhoto.photo?.id,
              }))
          : undefined;

      const createSelectedPhotos =
        mode === ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS
          ? currentSelectedPhotos
              .filter(
                slideshowPhoto =>
                  !(slideshow?.selected_photos?.records ?? []).find(
                    ({ photo }) => photo.id === slideshowPhoto.photo.id,
                  ),
              )
              .map(slideshowPhoto => ({
                photo_id: slideshowPhoto.photo?.id,
              }))
          : undefined;

      const deleteSelectedPhotos =
        mode === ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS
          ? (slideshow?.selected_photos?.records ?? [])
              .filter(
                ({ photo }) =>
                  !currentSelectedPhotos.find(
                    slideshowPhoto => photo.id === slideshowPhoto.photo.id,
                  ),
              )
              .map(slideshowPhoto => ({
                photo_id: slideshowPhoto.photo?.id,
              }))
          : undefined;

      await onSlideshowUpdate({
        status,
        mode,
        reviewPhotos: qrCode.isEnabled ? qrCode.withReviewPhotos : undefined,
        slideDurationMs,
        slideBackgroundColor,
        slideBackgroundAssetImage: slideBackgroundImage
          ? {
              asset_image_id:
                slideBackgroundImage.id ===
                slideshow?.slide_background_asset_image?.id
                  ? undefined
                  : slideBackgroundImage.id,
              existing_asset_image_id:
                slideshow?.slide_background_asset_image?.id,
            }
          : null,
        createExcludedPhotos,
        deleteExcludedPhotos,
        createSelectedPhotos,
        deleteSelectedPhotos,
        deletedSlideshowSlideIds: deletedSlideIds,
        qrCode: qrCode.isEnabled
          ? { is_enabled: true, display_callout: qrCode.withCallout }
          : undefined,
        ticker: tickerTape.message
          ? {
              message: tickerTape.message,
              color: tickerTape.color,
              background_color: tickerTape.backgroundColor,
            }
          : undefined,
        createPhotos,
        updateLayouts,
        createAutoLayouts: createAutoLayouts && isAllPhotos,
        createSelectedPhotoLayouts: createAutoLayouts && isSelectedPhotos,
      });
    } catch (e) {
      const errorString = getErrorString(e);
      if (isNativeView) {
        postNativeAppAlbumSlideshowDesignerUpdateError(errorString);
      } else {
        openAlert(errorString);
      }
    }
  };

  // Drag and Drop Context Related

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      delay: 50,
      tolerance: 5,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250,
      tolerance: 5,
    },
  });
  const onDragEnd = ({ active, over }) => {
    if (!active || !over) {
      return;
    }

    if (over.data?.current?.onDrop) {
      const { onDrop } = over.data.current;
      onDrop(active);
    }
  };

  const onDragStart = ({ active }) => {
    if (active.data?.current?.photo) {
      const { photo } = active.data.current;
      setDraggingSlidePhoto(photo);
    }
  };

  const activeThumb = (() => {
    if (activeSlide?.asset_image) {
      return activeSlide.asset_image;
    }
    if (activeSlide?.photo) {
      return getMaxThumb(activeSlide.photo.thumbnail_urls);
    }
    return null;
  })();

  const introLayout =
    slideshow?.intro_layout ?? slideshowSlides.find(isIntroLayout);

  const shouldRenderIntroLayoutConfigurator = () => {
    if (editingIntroLayout) {
      return true;
    }

    return isIntroLayout(activeSlide);
  };

  const shouldRenderLayoutsNullState = () => {
    if (!isAutoLayout) {
      return false;
    }

    if (
      activeControlPane === CONTROL_PANE.INTRO_SLIDE &&
      !activeSlide &&
      !introLayout
    ) {
      return true;
    }

    if (slideshowSlides.length === 0) {
      return true;
    }

    return false;
  };

  if (!user || !album || !slideshow || isPollingPendingLayouts) {
    return (
      <div className="w-100vw h-100vh d-flex align-items-center justify-content-center">
        <Spinner />
      </div>
    );
  }

  // If not the owner, you should not be here.
  if (!isAlbumOwner(user, album)) {
    history.replace(
      `${basename ? `/app/${basename}` : ''}/albums/${albumId}/slideshow`,
    );
  }

  const previewContainerClasses = classNames({
    'slideshow-designer-preview-container': true,
    'for-auto-layouts': isAutoLayout,
  });

  const slidesTotal =
    (slides?.total ?? 0) + (slideshow?.intro_layout && !isAutoLayout ? 1 : 0);

  return (
    <DndContext
      onDragEnd={onDragEnd}
      onDragStart={onDragStart}
      sensors={[mouseSensor, touchSensor]}
    >
      <DesignEditor className="slideshow-designer">
        <DesignEditorHeader>
          <DesignEditorHeaderLeftSlot>
            <Button
              btnStyle="link"
              className="mr-2"
              onClick={() => {
                if (isNativeView) {
                  postNativeAppAlbumSlideshowDesignerDismiss();
                } else {
                  history.goBack();
                }
              }}
            >
              <Angle direction="left" /> <span className="ml-1">Back</span>
            </Button>
          </DesignEditorHeaderLeftSlot>
          <DesignEditorHeaderRightSlot>
            {isAutoLayout && (
              <Button
                btnOutline
                disabled={saving}
                className="mr-2"
                onClick={async () => {
                  if (!canRegenerateSlideshow) {
                    openAlert({
                      type: 'info',
                      message: (
                        <div className="text-center">
                          <img
                            src={getAsset('/waldo-icon.svg')}
                            alt=""
                            className="mb-3"
                            width="48"
                          />
                          <p>
                            You will be able to regenerate your slideshow again
                            in{' '}
                            <span className="text-primary">
                              {regenerateThresholdTimeLeft} minutes.
                            </span>
                          </p>
                        </div>
                      ),
                    });
                    return;
                  }

                  if (saving) {
                    return;
                  }
                  try {
                    requestConfirm({
                      message: (
                        <div>
                          <h3 className="text-center">Regenerate Slideshow?</h3>
                          <p className="text-center">
                            Your slideshow will regenerate and new layouts will
                            be created.Your cover slide will not be changed. You
                            will not be able to regenerate for{' '}
                            {SLIDESHOW_REGENERATION_TRESHOLD_MINS} minutes
                            afterwards.
                          </p>
                        </div>
                      ),
                      cancelText: 'Go back',
                      confirmText: 'Regenerate',
                      onConfirm: async () => {
                        await onSlideshowUpdate({
                          createAutoLayouts: isAllPhotos,
                          createSelectedPhotoLayouts: isSelectedPhotos,
                          deleteAllLayouts: true,
                        });
                        await refetchSlideshowForLastRegenerated();
                        onStartPollingPendingLayouts({
                          pollingIntervalMs: 1000,
                        });
                      },
                      onCancel: () => {},
                    });
                  } catch (e) {
                    const errorString = getErrorString(e);
                    if (isNativeView) {
                      postNativeAppAlbumSlideshowDesignerUpdateError(
                        errorString,
                      );
                    } else {
                      openAlert(errorString);
                    }
                  }
                }}
              >
                Regenerate
              </Button>
            )}
            {slideshow?.status === ALBUM_SLIDESHOW_STATUS.PUBLISHED && (
              <Fragment>
                <Button
                  btnStyle="danger"
                  onClick={() =>
                    requestConfirm({
                      message: (
                        <div>
                          <h3 className="text-center">Unpublish Slideshow?</h3>
                          <p className="text-center">
                            Your slideshow will be saved as a draft after
                            unpublishing it. Others will not be able to view or
                            share this slideshow until it is published again.
                          </p>
                        </div>
                      ),
                      cancelText: 'Go back',
                      confirmText: 'Unpublish',
                      onConfirm: () => {
                        if (
                          isMemoryBookBool &&
                          currentUploadAccess === CONTRIBUTOR_UPLOAD_ACCESS.NONE
                        ) {
                          openModal(SLIDESHOW_UPLOAD_ACCESS_MODAL, {
                            isPublishing: false,
                            currentUploadAccess,
                            onSuccess: () => {
                              closeModal(SLIDESHOW_UPLOAD_ACCESS_MODAL);
                            },
                            onPublish: async ({ uploadAccess }) => {
                              if (saving) {
                                return;
                              }
                              try {
                                // Update the contributor assignments already existing.
                                await contributorAssignmentsUpdate({
                                  variables: {
                                    input: {
                                      hierarchy_item_id: album.id,
                                      upload_access: uploadAccess,
                                      view_access: CONTRIBUTOR_VIEW_ACCESS.OWN,
                                    },
                                  },
                                });
                                await onSlideshowUpdate({
                                  status: ALBUM_SLIDESHOW_STATUS.DRAFT,
                                  albumInput: {
                                    auto_contributor_settings: {
                                      view_access: CONTRIBUTOR_VIEW_ACCESS.OWN,
                                      upload_access: uploadAccess,
                                    },
                                  },
                                });
                              } catch (e) {
                                const errorString = getErrorString(e);
                                if (isNativeView) {
                                  postNativeAppAlbumSlideshowDesignerUpdateError(
                                    errorString,
                                  );
                                } else {
                                  openAlert(errorString);
                                }
                              }
                            },
                          });
                        } else {
                          onSaveSlideshow(
                            ALBUM_SLIDESHOW_STATUS.DRAFT,
                            slideshow?.mode ?? ALBUM_SLIDESHOW_MODE.ALL_PHOTOS,
                          );
                        }
                      },
                      onCancel: () => {},
                      confirmBtnStyle: 'danger',
                      confirmBtnOutline: true,
                    })
                  }
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Unpublish'}
                </Button>
                <Button
                  btnStyle="reset"
                  className="published-slideshow-play-btn"
                  data-for="play-slideshow"
                  data-place="top"
                  data-effect="solid"
                  data-tip
                  onClick={() => {
                    if (basename) {
                      history.push(
                        `/app/${basename}/albums/${album.id}/slideshow`,
                      );
                    } else {
                      history.push(`/albums/${album.id}/slideshow`);
                    }
                  }}
                >
                  <SlideshowIcon color="#0d84fe" />
                  <ReactTooltip id="play-slideshow">
                    Play slideshow
                  </ReactTooltip>
                </Button>
              </Fragment>
            )}
            {slideshow?.status === ALBUM_SLIDESHOW_STATUS.DRAFT && (
              <Button
                onClick={async () => {
                  try {
                    if (
                      isMemoryBookBool &&
                      currentUploadAccess === CONTRIBUTOR_UPLOAD_ACCESS.PUBLISH
                    ) {
                      openModal(SLIDESHOW_UPLOAD_ACCESS_MODAL, {
                        isPublishing: true,
                        currentUploadAccess,
                        onSuccess: () => {
                          closeModal(SLIDESHOW_UPLOAD_ACCESS_MODAL);
                          if (isNativeView) {
                            postNativeAppAlbumSlideshowDesignerShareDialog(
                              albumId,
                            );
                          } else {
                            openModal(CONTRIBUTOR_SHARE_MODAL, {
                              title: 'Your slideshow has been published!',
                            });
                          }
                        },
                        onPublish: async ({ uploadAccess }) => {
                          if (saving) {
                            return;
                          }
                          try {
                            // Update the contributor assignments already existing.
                            await contributorAssignmentsUpdate({
                              variables: {
                                input: {
                                  hierarchy_item_id: album.id,
                                  upload_access: uploadAccess,
                                  view_access: CONTRIBUTOR_VIEW_ACCESS.OWN,
                                },
                              },
                            });
                            await onSlideshowUpdate({
                              status: ALBUM_SLIDESHOW_STATUS.PUBLISHED,
                              albumInput: {
                                auto_contributor_settings: {
                                  view_access: CONTRIBUTOR_VIEW_ACCESS.OWN,
                                  upload_access: uploadAccess,
                                },
                              },
                            });
                          } catch (e) {
                            const errorString = getErrorString(e);
                            if (isNativeView) {
                              postNativeAppAlbumSlideshowDesignerUpdateError(
                                errorString,
                              );
                            } else {
                              openAlert(errorString);
                            }
                          }
                        },
                      });
                    } else {
                      await onSaveSlideshow(
                        ALBUM_SLIDESHOW_STATUS.PUBLISHED,
                        slideshow?.mode ?? ALBUM_SLIDESHOW_MODE.ALL_PHOTOS,
                      );
                      history.push(
                        `${
                          basename ? `/app/${basename}` : ''
                        }/albums/${albumId}/slideshow`,
                      );
                    }
                  } catch (e) {
                    const errorString = getErrorString(e);
                    if (isNativeView) {
                      postNativeAppAlbumSlideshowDesignerUpdateError(
                        errorString,
                      );
                    } else {
                      openAlert(errorString);
                    }
                  }
                }}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Publish'}
              </Button>
            )}
          </DesignEditorHeaderRightSlot>
        </DesignEditorHeader>

        <DesignEditorControls>
          <DesignEditorPrimaryControls>
            <DesignEditorPrimaryControlButton
              active={activeControlPane === CONTROL_PANE.PHOTOS}
              icon={<PhotosIcon />}
              title="Layout"
              onClick={() => {
                setActiveControlPane(CONTROL_PANE.PHOTOS);
              }}
            />
            {!isMemoryBookBool && (
              <DesignEditorPrimaryControlButton
                active={activeControlPane === CONTROL_PANE.QR_CODE}
                icon={<QRIcon />}
                title="QR Code"
                onClick={() => {
                  setActiveControlPane(CONTROL_PANE.QR_CODE);
                }}
              />
            )}
            <DesignEditorPrimaryControlButton
              active={activeControlPane === CONTROL_PANE.TICKER_TAPE}
              icon={<TickerTapeIcon />}
              title="Ticker Tape"
              onClick={() => {
                setActiveControlPane(CONTROL_PANE.TICKER_TAPE);
              }}
            />
            <DesignEditorPrimaryControlButton
              active={activeControlPane === CONTROL_PANE.SLIDE_DURATION}
              icon={<StopWatchIcon />}
              title="Speed"
              onClick={() => {
                setActiveControlPane(CONTROL_PANE.SLIDE_DURATION);
              }}
            />
            <DesignEditorPrimaryControlButton
              active={activeControlPane === CONTROL_PANE.BACKGROUND}
              icon={<BackgroundIcon />}
              title="Background"
              onClick={() => {
                setActiveControlPane(CONTROL_PANE.BACKGROUND);
              }}
            />
          </DesignEditorPrimaryControls>
          <DesignEditorSecondaryControls
            className={classNames({
              'd-none': (() => {
                if (activeControlPane === CONTROL_PANE.SLIDES) {
                  return true;
                }

                if (activeControlPane === CONTROL_PANE.INTRO_SLIDE) {
                  return true;
                }

                return false;
              })(),
            })}
          >
            <SecondaryPhotosView
              visible={activeControlPane === CONTROL_PANE.PHOTOS}
              albumId={albumId}
              slideshow={slideshow}
              slideshowSlides={slideshowSlides}
              slidesTotal={slidesTotal}
              slidesOffset={Math.max((slides?.records?.length ?? 10) - 10, 0)}
              hasIntroSlide={!!introLayout}
              defaultAlbumPhotoFilter={defaultAlbumPhotoFilter}
              onIntroSlideCreate={() => {
                setActiveSlide(null);
                setEditingIntroLayout(true);
              }}
              currentSlideId={
                activeSlide?.photo?.id ??
                activeSlide?.asset_image?.id ??
                activeSlide?.account_card?.id
              }
              onSlideshowUpdate={onSlideshowUpdate}
              onThumbnailClick={onThumbnailClick}
              onReorderSlides={reorderedSlides => {
                setSlideshowSlides(reorderedSlides);
                onSaveSlideshow(
                  slideshow?.status ?? ALBUM_SLIDESHOW_STATUS.DRAFT,
                  slideshow?.mode ?? ALBUM_SLIDESHOW_MODE.ALL_PHOTOS,
                  excludedPhotos,
                  selectedPhotos,
                  !isAutoLayout
                    ? reorderedSlides
                        .filter(({ account_card }) => !account_card)
                        .map(({ photo }, index) => ({
                          photo_id: photo.id,
                          display_order: index,
                        }))
                    : [],
                  isAutoLayout
                    ? reorderedSlides.map(({ account_card }, index) => ({
                        account_card_id: account_card.id,
                        display_order: index,
                      }))
                    : [],
                );
              }}
              onOrderPhotos={() => {
                onPlaybackStop();
                openModal(ALBUM_SLIDESHOW_DESIGNER_PHOTO_ORDERING_MODAL);
              }}
              onMoveSlide={({ slideIdx, direction }) => {
                const reorderedSlides = arrayMove(
                  slideshowSlides,
                  slideIdx,
                  slideIdx + direction,
                );
                setSlideshowSlides(reorderedSlides);
                onSaveSlideshow(
                  slideshow?.status ?? ALBUM_SLIDESHOW_STATUS.DRAFT,
                  slideshow?.mode ?? ALBUM_SLIDESHOW_MODE.ALL_PHOTOS,
                  excludedPhotos,
                  selectedPhotos,
                  !isAutoLayout
                    ? reorderedSlides
                        .filter(({ asset_image }) => !asset_image)
                        .map(({ photo }, index) => ({
                          photo_id: photo.id,
                          display_order: index,
                        }))
                    : [],
                  isAutoLayout
                    ? reorderedSlides
                        .filter(
                          slideshowSlide =>
                            !slideshowSlide.asset_image &&
                            !isIntroLayout(slideshowSlide),
                        )
                        .map(({ account_card }, index) => ({
                          account_card_id: account_card.id,
                          display_order: index,
                        }))
                    : [],
                );
              }}
              onDeletePhoto={async ({ photoId, photoIdx, isIntroSlide }) => {
                if (slideshow && isIntroSlide) {
                  await onSlideshowUpdate({
                    introAssetImage: null,
                  });
                  setActiveSlide(slideshowSlides?.[0] ?? null);
                  return;
                }
                if (slideshow) {
                  await onSlideshowUpdate({
                    createExcludedPhotos:
                      slideshow.mode === ALBUM_SLIDESHOW_MODE.ALL_PHOTOS
                        ? [{ photo_id: photoId }]
                        : undefined,
                    deleteSelectedPhotos:
                      slideshow.mode === ALBUM_SLIDESHOW_MODE.SELECTED_PHOTOS
                        ? [{ photo_id: photoId }]
                        : undefined,
                  });
                }
                const nextSlideId = (() => {
                  if (
                    photoIdx < slideshowSlides.length - 1 &&
                    slideshowSlides.length > 1
                  ) {
                    return photoIdx + 1;
                  }
                  if (
                    photoIdx === slideshowSlides.length - 1 &&
                    slideshowSlides.length > 1
                  ) {
                    return photoIdx - 1;
                  }
                  return null;
                })();
                if (nextSlideId === null) {
                  setActiveSlide(null);
                } else {
                  setActiveSlide(slideshowSlides[nextSlideId]);
                }
              }}
              onDeleteLayout={async ({ account_card_id, slideIdx }) => {
                if (saving) {
                  return;
                }
                try {
                  await onSlideshowUpdate({
                    deleteLayouts: [{ account_card_id }],
                  });
                  const nextSlideId = (() => {
                    if (
                      slideIdx < slideshowSlides.length - 1 &&
                      slideshowSlides.length > 1
                    ) {
                      return slideIdx + 1;
                    }
                    if (
                      slideIdx === slideshowSlides.length - 1 &&
                      slideshowSlides.length > 1
                    ) {
                      return slideIdx - 1;
                    }
                    return null;
                  })();
                  if (nextSlideId === null) {
                    setActiveSlide(null);
                  } else {
                    setActiveSlide(slideshowSlides[nextSlideId]);
                  }
                } catch (e) {
                  const errorString = getErrorString(e);
                  if (isNativeView) {
                    postNativeAppAlbumSlideshowDesignerUpdateError(errorString);
                  } else {
                    openAlert(errorString);
                  }
                }
              }}
              loadingMore={loadingMore}
              onLoadMore={onLoadMore}
            />
            {!isMemoryBookBool && (
              <SecondaryQRCodeView
                qrCode={qrCode}
                visible={activeControlPane === CONTROL_PANE.QR_CODE}
                onUpdateQRCode={async ({ withReviewPhotos, enabled }) => {
                  if (saving) {
                    return;
                  }
                  try {
                    await onSlideshowUpdate({
                      reviewPhotos: withReviewPhotos,
                      qrCode: {
                        is_enabled: enabled,
                        display_callout: true,
                      },
                    });
                  } catch (e) {
                    const errorString = getErrorString(e);
                    if (isNativeView) {
                      postNativeAppAlbumSlideshowDesignerUpdateError(
                        errorString,
                      );
                    } else {
                      openAlert(errorString);
                    }
                  }
                }}
              />
            )}
            <SecondaryTickerTapeView
              visible={activeControlPane === CONTROL_PANE.TICKER_TAPE}
              tickerTape={tickerTape}
              tickerTapeEnabled={tickerTapeEnabled}
              onToggleTickerTapeEnabled={() =>
                setTickerTapeEnabled(prev => !prev)
              }
              onUpdateTickerTape={async newTicker => {
                if (saving) {
                  return;
                }
                try {
                  await onSlideshowUpdate({
                    ticker: newTicker,
                  });
                } catch (e) {
                  const errorString = getErrorString(e);
                  if (isNativeView) {
                    postNativeAppAlbumSlideshowDesignerUpdateError(errorString);
                  } else {
                    openAlert(errorString);
                  }
                }
              }}
            />
            <SecondarySlideDurationView
              visible={activeControlPane === CONTROL_PANE.SLIDE_DURATION}
              defaultDuration={slideDurationMs}
              onDefaultDurationChange={async newMsDuration => {
                onPlaybackStop();
                if (saving) {
                  return;
                }
                try {
                  await onSlideshowUpdate({
                    slideDurationMs: newMsDuration,
                  });
                } catch (e) {
                  const errorString = getErrorString(e);
                  if (isNativeView) {
                    postNativeAppAlbumSlideshowDesignerUpdateError(errorString);
                  } else {
                    openAlert(errorString);
                  }
                }
              }}
            />
            <SecondaryBackgroundView
              visible={activeControlPane === CONTROL_PANE.BACKGROUND}
              saving={saving}
              backgroundColor={slideBackgroundColor}
              backgroundImage={slideBackgroundImage}
              onBackgroundColorChange={async newColor => {
                if (saving) {
                  return;
                }
                try {
                  await onSlideshowUpdate({
                    slideBackgroundColor: newColor,
                  });
                } catch (e) {
                  const errorString = getErrorString(e);
                  if (isNativeView) {
                    postNativeAppAlbumSlideshowDesignerUpdateError(errorString);
                  } else {
                    openAlert(errorString);
                  }
                }
              }}
              onBackgroundImageChange={async newAssetImage => {
                if (saving) {
                  return;
                }
                try {
                  await onSlideshowUpdate({
                    slideBackgroundAssetImage: newAssetImage
                      ? {
                          asset_image_id:
                            newAssetImage.id ===
                            slideshow?.slide_background_asset_image?.id
                              ? undefined
                              : newAssetImage.id,
                          existing_asset_image_id:
                            slideshow?.slide_background_asset_image?.id,
                        }
                      : null,
                  });
                } catch (e) {
                  const errorString = getErrorString(e);
                  if (isNativeView) {
                    postNativeAppAlbumSlideshowDesignerUpdateError(errorString);
                  } else {
                    openAlert(errorString);
                  }
                }
              }}
            />
          </DesignEditorSecondaryControls>
        </DesignEditorControls>

        <DesignEditorPreviewPane
          className={classNames({
            'no-slides': slidesTotal === 0,
          })}
        >
          <DesignEditorPreviewContent>
            {shouldRenderIntroLayoutConfigurator() ? (
              <IntroLayoutConfigurator
                albumId={albumId}
                accountCardId={introLayout?.account_card?.id}
                onDelete={async () => {
                  if (saving) {
                    return;
                  }
                  try {
                    await onSlideshowUpdate({
                      deleteLayouts: [
                        { account_card_id: introLayout?.account_card?.id },
                      ],
                    });
                    setEditingIntroLayout(false);
                  } catch (e) {
                    const errorString = getErrorString(e);
                    if (isNativeView) {
                      postNativeAppAlbumSlideshowDesignerUpdateError(
                        errorString,
                      );
                    } else {
                      openAlert(errorString);
                    }
                  }
                }}
                onAutosave={async ({ accountCard }) => {
                  if (saving) {
                    return;
                  }

                  if (accountCard.id === introLayout?.account_card?.id) {
                    return;
                  }

                  try {
                    // Set this pseudo slide in order to make sure the new card is maintained as active.
                    setActiveSlide({
                      display_order: -1,
                      account_card: accountCard,
                    });
                    await onSlideshowUpdate({
                      createLayouts: [
                        {
                          display_order: -1,
                          account_card_id: accountCard.id,
                        },
                      ],
                      deleteLayouts: introLayout
                        ? [{ account_card_id: introLayout.account_card.id }]
                        : undefined,
                    });
                    setEditingIntroLayout(false);
                  } catch (e) {
                    const errorString = getErrorString(e);
                    if (isNativeView) {
                      postNativeAppAlbumSlideshowDesignerUpdateError(
                        errorString,
                      );
                    } else {
                      openAlert(errorString);
                    }
                  }
                }}
                tickerTape={
                  <Fragment>
                    {tickerTapeEnabled && (
                      <EditableTickerTape
                        tickerTape={tickerTape}
                        inputRef={tickerTapeMessageRef}
                        isStatic={
                          activeControlPane === CONTROL_PANE.TICKER_TAPE
                        }
                        onUpdateTickerTape={async newTicker => {
                          if (saving) {
                            return;
                          }
                          try {
                            await onSlideshowUpdate({
                              ticker: newTicker,
                            });
                          } catch (e) {
                            const errorString = getErrorString(e);
                            if (isNativeView) {
                              postNativeAppAlbumSlideshowDesignerUpdateError(
                                errorString,
                              );
                            } else {
                              openAlert(errorString);
                            }
                          }
                        }}
                      />
                    )}
                  </Fragment>
                }
                slideBackgroundColor={slideBackgroundColor}
                slideBackgroundImage={slideBackgroundImage}
              />
            ) : (
              <div
                className={previewContainerClasses}
                style={{
                  backgroundColor: slideBackgroundColor,
                  backgroundImage: slideBackgroundImage
                    ? `url(${slideBackgroundImage.url})`
                    : '',
                }}
              >
                {shouldRenderLayoutsNullState() ? (
                  <div className="slideshow-designer-null-state">
                    <img
                      alt="Waldo logo"
                      className="mb-1 grayscale-faded"
                      width="48px !important"
                      src={getAsset('/waldo-icon.svg')}
                    />
                    <p className="text-center mb-0">
                      {(album?.photos.total ?? 0) === 0 ? (
                        <>No photos here yet</>
                      ) : (
                        <>
                          Your slides are processing!
                          <br />
                          This may take a few minutes. <br />
                        </>
                      )}
                    </p>
                  </div>
                ) : (
                  <Fragment>
                    {activeSlide?.account_card && (
                      <Fragment>
                        {isIntroLayout(activeSlide) && (
                          <IntroSlideLabel
                            albumId={album.id}
                            style={{ opacity: photoOpacity }}
                          />
                        )}
                        <AccountCardEditor
                          accountCardId={activeSlide.account_card.id}
                          photoOpacity={photoOpacity}
                        />
                      </Fragment>
                    )}
                    {activeThumb && (
                      <img
                        key={activeThumb.url}
                        src={activeThumb.url}
                        alt="Active Slide"
                        style={{ opacity: photoOpacity }}
                      />
                    )}
                  </Fragment>
                )}
                {qrCode?.isEnabled && (
                  <div
                    tabIndex={0}
                    role="button"
                    className="qr-code-container"
                    onClick={() => {
                      openModal(QR_CODE_INSERT_MODAL, {
                        enabled: !!qrCode.isEnabled,
                        insertCallout: true,
                        includeLogo: !!qrCode?.withLogo,
                        reviewPhotos: !!qrCode?.withReviewPhotos,
                      });
                    }}
                  >
                    <QRCode
                      id="got-photos-qr"
                      value={qrCode.url}
                      renderAs="canvas"
                      size={isMobile ? 60 : 100}
                      imageSettings={
                        qrCode.withLogo
                          ? {
                              src: qrCode.logo.url,
                              height: 64,
                              width: 64,
                            }
                          : undefined
                      }
                    />
                    {qrCode.withCallout && (
                      <div className="qr-code-callout">SCAN TO ADD PHOTOS</div>
                    )}
                  </div>
                )}
                {tickerTapeEnabled && (
                  <EditableTickerTape
                    tickerTape={tickerTape}
                    inputRef={tickerTapeMessageRef}
                    isStatic={activeControlPane === CONTROL_PANE.TICKER_TAPE}
                    onUpdateTickerTape={async newTicker => {
                      if (saving) {
                        return;
                      }
                      try {
                        await onSlideshowUpdate({
                          ticker: newTicker,
                        });
                      } catch (e) {
                        const errorString = getErrorString(e);
                        if (isNativeView) {
                          postNativeAppAlbumSlideshowDesignerUpdateError(
                            errorString,
                          );
                        } else {
                          openAlert(errorString);
                        }
                      }
                    }}
                  />
                )}
              </div>
            )}
          </DesignEditorPreviewContent>
        </DesignEditorPreviewPane>
        <AlbumSlideshowShareModal
          album={album}
          albumId={albumId}
          designerOptions={{
            onPresent: () => {
              history.push(
                `${
                  basename ? `/app/${basename}` : ''
                }/albums/${albumId}/slideshow?present=1`,
              );

              closeModal(ALBUM_SLIDESHOW_SHARE_MODAL);
            },
            // If we don't have a basename, going back to the event will not be available
            // this is to avoid directing the user to the wrong basename, which will, in
            // the end, result in the user going back to the home page.
            onShare: () => {
              openModal(CONTRIBUTOR_SHARE_MODAL);
              closeModal(ALBUM_SLIDESHOW_SHARE_MODAL);
            },
          }}
        />
        <PhotoOrderingOverlayModal
          albumId={albumId}
          onChange={async ({ slideshowPhotos }) => {
            await onSaveSlideshow(
              slideshow?.status ?? ALBUM_SLIDESHOW_STATUS.DRAFT,
              slideshow?.mode ?? ALBUM_SLIDESHOW_MODE.ALL_PHOTOS,
              // Encapsulate the photos, since we want an object
              // akin to AlbumSlideshowPhoto, which encapsulates a photo.
              [],
              [],
              slideshowPhotos.map((slideshowPhoto, index) => ({
                photo_id: slideshowPhoto.photo.id,
                display_order: index,
              })),
              [],
              true,
            );
          }}
        />
        <SlideshowUploadAccessModal />
        <ContributorShare />
        <ContactCircleSaveModal />
        <ContactCircleShareModal />
        <AlbumFolderUploadSuccessModal albumFolder={album?.album_folder} />
      </DesignEditor>
      <DragOverlay>
        {draggingSlidePhoto && (
          <div className="slideshow-photo-order-overlay">
            <Photo photo={draggingSlidePhoto} />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};

AlbumSlideshowDesignerView.propTypes = propTypes;

export default withRouter(AlbumSlideshowDesignerView);
